import React, { useState, useMemo, useCallback } from 'react';
import {
  Filter, X, TrendingUp, Stethoscope, Briefcase, BarChart3,
  Flame, Clock, CheckSquare, Calendar, Plane, DollarSign,
  AlertCircle, FileText, FileX, Image as ImgIcon,
} from 'lucide-react';

/* ──────────────────────────────────────────────────────────────────────────
 * ContactSmartFilters
 *
 * Phase C: filter chips + smart view presets.
 *
 * Renders above the existing search/type filter bar on ContactsTab.
 * Filters operate CLIENT-SIDE on the already-loaded contacts list — instant
 * toggle, no round-trip. Predicates use the new Phase A fields.
 *
 * Smart Views (preset chip sets per role):
 *   SALES VIEW            — hot, notContacted24h, financingNeeded, readyToTravel
 *   DOCTOR REVIEW VIEW    — mriUploaded, candidateExcellent, severeOrMod
 *   CONCIERGE VIEW        — clinicAssigned, travelNeeded, missingDocs, passportPending
 *   MANAGEMENT VIEW       — hot, highPriority, noNextAction, paymentFinancing
 *
 * Plus standalone chips the user can toggle individually.
 *
 * Returns:
 *   - <ContactSmartFilters chips={...} setChips={...} />
 *   - applyChipFilters(contacts, chips) — utility to filter the list
 * ────────────────────────────────────────────────────────────────────────── */

/* ─── Chip catalogue ─────────────────────────────────────────────────────── */

// Each chip: { id, label, icon, group, predicate(contact) }
// Groups colour-code the chip and group them in the strip.

export const FILTER_CHIPS = [
  // SALES
  { id: 'hot', label: 'Hot Leads', icon: Flame, group: 'sales',
    predicate: c => c.lead_temperature === 'hot' || c._leadBand === 'hot' },
  { id: 'notContacted24h', label: 'Not Contacted 24h', icon: Clock, group: 'sales',
    predicate: c => {
      if (!c.last_contact_date) return true;
      return Date.now() - new Date(c.last_contact_date).getTime() > 24 * 60 * 60 * 1000;
    } },
  { id: 'noNextAction', label: 'No Next Action', icon: AlertCircle, group: 'sales',
    predicate: c => !c.next_action },
  { id: 'highIntent', label: 'High Intent (≥7)', icon: TrendingUp, group: 'sales',
    predicate: c => (c.patient_intent_score || 0) >= 7 },
  { id: 'financingNeeded', label: 'Financing Needed', icon: DollarSign, group: 'sales',
    predicate: c => c.financial_readiness === 'financing_needed' },
  { id: 'highPriority', label: 'Urgent / High Priority', icon: AlertCircle, group: 'sales',
    predicate: c => c.followup_priority === 'urgent' || c.followup_priority === 'high' },

  // OPERATIONAL
  { id: 'readyToTravel', label: 'Ready to Travel', icon: Plane, group: 'operational',
    predicate: c => c.passport_status === 'valid' &&
      ['deposit_paid', 'partial_paid', 'paid_in_full'].includes(c.payment_status) },
  { id: 'travelNotCoordinated', label: 'Travel Not Coordinated', icon: Plane, group: 'operational',
    predicate: c => Array.isArray(c.travel_coordination_status) && c.travel_coordination_status.length > 0 },
  { id: 'missingDocs', label: 'Missing Documents', icon: FileX, group: 'operational',
    predicate: c => !c.document_status || (Array.isArray(c.document_status) && c.document_status.length === 0) },
  { id: 'noDeposits', label: 'No Deposit Paid', icon: DollarSign, group: 'operational',
    predicate: c => !c.payment_status || c.payment_status === 'no_payment' },
  { id: 'highNoShowRisk', label: 'High No-Show Risk', icon: AlertCircle, group: 'operational',
    predicate: c => c.no_show_risk === 'high' },

  // MEDICAL / DOCTOR
  { id: 'mriUploaded', label: 'MRI Uploaded', icon: ImgIcon, group: 'medical',
    predicate: c => c.mri_xray_uploaded === true },
  { id: 'mriPending', label: 'MRI Pending', icon: ImgIcon, group: 'medical',
    predicate: c => !c.mri_xray_uploaded },
  { id: 'candidateExcellent', label: 'Excellent Candidate', icon: CheckSquare, group: 'medical',
    predicate: c => c.candidate_status === 'excellent' },
  { id: 'severeOrMod', label: 'Severe / Moderate', icon: AlertCircle, group: 'medical',
    predicate: c => c.severity_level === 'severe' || c.severity_level === 'moderate' },
];

/* ─── Smart view presets ─────────────────────────────────────────────────── */

export const SMART_VIEWS = [
  { id: 'all',         label: 'All',         icon: Filter,     chips: [] },
  { id: 'sales',       label: 'Sales',       icon: TrendingUp, chips: ['hot', 'notContacted24h', 'highIntent', 'noNextAction'] },
  { id: 'doctor',      label: 'Doctor Review', icon: Stethoscope, chips: ['mriUploaded', 'candidateExcellent', 'severeOrMod'] },
  { id: 'concierge',   label: 'Concierge',   icon: Briefcase,  chips: ['readyToTravel', 'travelNotCoordinated', 'missingDocs'] },
  { id: 'management',  label: 'Management',  icon: BarChart3,  chips: ['hot', 'highPriority', 'noNextAction', 'noDeposits'] },
];

/* ─── Style + colour ─────────────────────────────────────────────────────── */

const GROUP_COLOR = {
  sales:       { color: '#A855F7', bg: '#F3E8FF', activeBg: '#A855F7', activeColor: '#FFFFFF' },
  operational: { color: '#F59E0B', bg: '#FEF3C7', activeBg: '#F59E0B', activeColor: '#FFFFFF' },
  medical:     { color: '#10B981', bg: '#D1FAE5', activeBg: '#10B981', activeColor: '#FFFFFF' },
};

/* ─── Apply filters utility ──────────────────────────────────────────────── */

export function applyChipFilters(contacts, activeChipIds) {
  if (!Array.isArray(contacts)) return [];
  if (!activeChipIds || activeChipIds.length === 0) return contacts;
  const chips = FILTER_CHIPS.filter(c => activeChipIds.includes(c.id));
  if (chips.length === 0) return contacts;
  return contacts.filter(contact => chips.every(chip => chip.predicate(contact)));
}

/* ─── Main component ─────────────────────────────────────────────────────── */

export default function ContactSmartFilters({ chips, setChips, totalCount, filteredCount }) {
  const [activeView, setActiveView] = useState('all');

  const toggleChip = useCallback((id) => {
    setChips(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
    setActiveView('custom'); // any manual toggle moves us off a preset
  }, [setChips]);

  const selectView = useCallback((viewId) => {
    const view = SMART_VIEWS.find(v => v.id === viewId);
    if (!view) return;
    setActiveView(viewId);
    setChips(view.chips);
  }, [setChips]);

  const clearAll = useCallback(() => {
    setChips([]);
    setActiveView('all');
  }, [setChips]);

  const groupedChips = useMemo(() => {
    const g = { sales: [], operational: [], medical: [] };
    for (const chip of FILTER_CHIPS) g[chip.group].push(chip);
    return g;
  }, []);

  const filtered = chips.length > 0;

  return (
    <div style={{
      marginBottom: 12, padding: 12, borderRadius: 12,
      background: '#F8FAFC', border: '1px solid #E2E8F0',
    }}>
      {/* Smart View preset row */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: filtered ? 10 : 0, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 4 }}>
          View:
        </span>
        {SMART_VIEWS.map(v => {
          const Icon = v.icon;
          const isActive = activeView === v.id;
          return (
            <button key={v.id} onClick={() => selectView(v.id)} style={{
              padding: '5px 10px', borderRadius: 8,
              border: `1px solid ${isActive ? '#2E8B98' : '#CBD5E1'}`,
              background: isActive ? '#2E8B98' : '#FFFFFF',
              color: isActive ? '#FFFFFF' : '#475569',
              fontSize: 11, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
              <Icon size={12} />{v.label}
            </button>
          );
        })}
        {activeView === 'custom' && (
          <span style={{
            padding: '4px 8px', borderRadius: 6, background: '#FEF3C7',
            color: '#7C2D12', fontSize: 10, fontWeight: 600,
          }}>Custom</span>
        )}
        <div style={{ flex: 1 }} />
        {filtered && (
          <span style={{ fontSize: 11, color: '#64748B' }}>
            <strong style={{ color: '#0F172A' }}>{filteredCount}</strong>
            <span> of {totalCount} matching</span>
          </span>
        )}
        {filtered && (
          <button onClick={clearAll} style={{
            padding: '4px 8px', borderRadius: 6, border: '1px solid #CBD5E1',
            background: '#FFFFFF', color: '#475569', fontSize: 11, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <X size={11} /> Clear
          </button>
        )}
      </div>

      {/* Chip groups */}
      {['sales', 'operational', 'medical'].map(groupId => (
        <div key={groupId} style={{
          display: 'flex', gap: 6, alignItems: 'center',
          marginTop: 8, flexWrap: 'wrap',
        }}>
          <span style={{
            fontSize: 9, fontWeight: 700, color: GROUP_COLOR[groupId].color,
            textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 4, minWidth: 70,
          }}>
            {groupId === 'medical' ? 'Doctor' : groupId.charAt(0).toUpperCase() + groupId.slice(1)}
          </span>
          {groupedChips[groupId].map(chip => {
            const Icon = chip.icon;
            const isActive = chips.includes(chip.id);
            const palette = GROUP_COLOR[chip.group];
            return (
              <button key={chip.id} onClick={() => toggleChip(chip.id)} style={{
                padding: '4px 10px', borderRadius: 14,
                border: `1px solid ${isActive ? palette.activeBg : palette.color + '55'}`,
                background: isActive ? palette.activeBg : palette.bg,
                color: isActive ? palette.activeColor : palette.color,
                fontSize: 11, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4,
                transition: 'all 0.1s ease',
              }}>
                <Icon size={11} />{chip.label}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
